/**
 * AutoLinkModal Component
 * Draggable modal for adjusting auto-link radius with real-time preview
 */

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { dia } from '@joint/core';
import { TemplateRelationType } from '../../../map-editor-entities/schema/templateSchema';
import { useObjectTypeStore } from '../../../map-editor-shared/store/objectTypeStore';
import { resolveLayerToTemplateKey } from '../../../map-editor-shared/lib/layerUtils';
import styles from './AutoLinkModal.module.css';
import { log, LogCategory } from '../../../map-editor-shared/lib/logger';
import { parseCardinality } from '../../../map-editor-features/editor/lib/relationshipUtils';
import { graphDistanceScale } from '../../../map-editor-features/editor/lib/normalizeMapScale';
import {
  isZoneCctvRelation,
  planZoneCctvIdRelations,
  ZONE_CCTV_ID_RELATION,
} from '../../../map-editor-features/editor/lib/zoneCctvIdRelations';
import { involvesGuideBoard } from '../../../map-editor-features/editor/lib/guideBoardDirectionRelations';
import { getFlag } from '../../../config/featureFlags';
import { getActiveCanvasRenderer } from '../../../map-editor-features/viewer/canvas/activeRenderer';

// 슬라이더 상한선. template 의 maxDistance 와 분리해 UI 에서만 더 넓게 조정 가능하게 한다.
// 직접 입력값에 맞춰 슬라이더 범위도 확장한다.
const AUTO_LINK_SLIDER_MAX_PX = 8000;

interface AutoLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (
    adjustedDistances: Record<string, number>,
    enabledRelations: Record<string, boolean>,
  ) => Promise<void> | void;
  relationTypes: Record<string, TemplateRelationType>;
  template?: any;
  graph: dia.Graph | null;
  paper: dia.Paper | null;
}

interface CirclePreview {
  x: number;
  y: number;
  radius: number;
  color: string;
  relationKey: string;
}

interface LinePreview {
  srcX: number;
  srcY: number;
  tgtX: number;
  tgtY: number;
  color: string;
  relationKey: string;
}

export function AutoLinkModal({
  isOpen,
  onClose,
  onConfirm,
  relationTypes,
  template,
  graph,
  paper,
}: AutoLinkModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: 100, y: 100 });

  // Use useMemo to prevent recreating this array on every render.
  // 안내현황판 관계는 반경이 아니라 좌/우 영역 드래그로 맺으므로 반경 모달에서 제외.
  const autoLinkRelations = useMemo(
    () =>
      Object.entries(relationTypes).filter(
        ([_, config]) =>
          config.autoLink &&
          !involvesGuideBoard(config.sourceType, config.targetType) &&
          !isZoneCctvRelation(config.sourceType, config.targetType),
      ),
    [relationTypes],
  );

  const zonePlan = useMemo(
    () => (graph && isOpen ? planZoneCctvIdRelations(graph) : null),
    [graph, isOpen],
  );
  // 미연결 카메라는 적용 전에 전부 보여준다 — 적용 후 alert 로 잘라 보여주면
  // 어떤 카메라를 손봐야 하는지 알 수 없다.
  const zoneUnmatched = useMemo(
    () => (zonePlan?.assignments || []).filter((item) => !item.zone),
    [zonePlan],
  );
  const zoneLinkedCount = (zonePlan?.assignments.length || 0) - zoneUnmatched.length;

  // Initialize distances from config
  const [distances, setDistances] = useState<Record<string, number>>(() => {
    const initial: Record<string, number> = {};
    Object.entries(relationTypes).forEach(([key, config]) => {
      if (config.autoLink) {
        initial[key] = config.autoLink.maxDistance;
      }
    });
    return initial;
  });

  const [distanceDrafts, setDistanceDrafts] = useState<Record<string, string>>({});

  const [objectCounts, setObjectCounts] = useState<
    Record<string, { sources: number; targets: number; estimatedLinks: number }>
  >({});
  const [circles, setCircles] = useState<CirclePreview[]>([]);
  const [lines, setLines] = useState<LinePreview[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Track paper transform for overlay synchronization
  const [paperTransform, setPaperTransform] = useState({ tx: 0, ty: 0, sx: 1 });

  useEffect(() => {
    if (!paper || !isOpen) return;

    // CANVAS_EDITOR 가 켜져 있으면 미리보기를 캔버스 렌더러가 직접 그린다(아래
    // useEffect). paper 는 얼려진 채라 transform 을 따라갈 필요가 없다.
    if (getFlag('CANVAS_EDITOR')) return;

    const update = () => {
      const s = paper.scale();
      const t = paper.translate();
      setPaperTransform({ tx: t.tx, ty: t.ty, sx: s.sx });
    };
    update();
    paper.on('scale', update);
    paper.on('translate', update);
    return () => {
      paper.off('scale', update);
      paper.off('translate', update);
    };
  }, [paper, isOpen]);


  // Individual preview toggle for each relation type (default: all on — 열자마자
  // 전체 관계선이 맵에 보여야 무엇이 바뀌는지 판단할 수 있다)
  const [showPreview, setShowPreview] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    Object.keys(relationTypes).forEach((key) => {
      if (
        relationTypes[key].autoLink &&
        !isZoneCctvRelation(relationTypes[key].sourceType, relationTypes[key].targetType) &&
        !involvesGuideBoard(relationTypes[key].sourceType, relationTypes[key].targetType)
      ) {
        initial[key] = true;
      }
    });
    return initial;
  });

  // CANVAS_EDITOR: 미리보기를 DOM 오버레이 대신 캔버스 렌더러에 넘긴다. 렌더러가
  // 카메라를 소유하므로 좌표를 world 그대로 주면 줌/이동에 자동으로 따라온다.
  useEffect(() => {
    if (!getFlag('CANVAS_EDITOR')) return;
    const renderer = getActiveCanvasRenderer();
    if (!renderer) return;
    if (!isOpen) {
      renderer.setAutoLinkPreviewOverlay(null);
      return;
    }
    // `?? true` 는 체크박스(checked={showPreview[key] ?? true})와 같은 기본값이다.
    // 모달이 뜬 뒤에 관계 타입이 늘면 showPreview 에 키가 없는데(초기화는 마운트
    // 시 한 번뿐), 그러면 체크박스는 켜진 것처럼 보이면서 여기서만 걸러져
    // 화면에는 아무것도 안 그려진다.
    renderer.setAutoLinkPreviewOverlay({
      lines: lines
        .filter((l) => showPreview[l.relationKey] ?? true)
        .map((l) => ({ x1: l.srcX, y1: l.srcY, x2: l.tgtX, y2: l.tgtY, color: l.color })),
      circles: circles
        .filter((c) => showPreview[c.relationKey] ?? true)
        .map((c) => ({ x: c.x, y: c.y, radius: c.radius, color: c.color })),
    });
    return () => renderer.setAutoLinkPreviewOverlay(null);
  }, [isOpen, lines, circles, showPreview]);

  // Individual enable toggle for each relation type (default: all disabled)
  const [enabledRelations, setEnabledRelations] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = { [ZONE_CCTV_ID_RELATION]: false };
    Object.keys(relationTypes).forEach((key) => {
      if (
        relationTypes[key].autoLink &&
        !isZoneCctvRelation(relationTypes[key].sourceType, relationTypes[key].targetType) &&
        !involvesGuideBoard(relationTypes[key].sourceType, relationTypes[key].targetType)
      ) {
        initial[key] = false; // Default: all disabled
      }
    });
    return initial;
  });

  const invalidDistance = autoLinkRelations.some(
    ([key]) =>
      enabledRelations[key] &&
      distanceDrafts[key] !== undefined &&
      (!Number.isFinite(Number(distanceDrafts[key])) || Number(distanceDrafts[key]) < 1),
  );

  // Check if all relations are enabled (useMemo must be at top level)
  const allRelationsEnabled = useMemo(
    () => Object.values(enabledRelations).every((enabled) => enabled),
    [enabledRelations],
  );

  // Check if all previews are enabled (useMemo must be at top level)
  const allPreviewsEnabled = useMemo(
    () => Object.values(showPreview).every((enabled) => enabled),
    [showPreview],
  );

  // Calculate object counts and circle previews
  useEffect(() => {
    if (!graph || !paper || !template?.objectTypes || !isOpen) return;

    // Build UUID mapping
    const uuidToTemplateType = new Map<string, string>();
    const objectTypes = useObjectTypeStore.getState().types;

    objectTypes.forEach((objType) => {
      // Try 1: 직접 매치 (displayName / name / key).
      let templateEntry = Object.entries(template.objectTypes).find(
        ([key, tmplType]: [string, any]) =>
          tmplType.displayName === objType.name ||
          tmplType.name === objType.name ||
          key === objType.name,
      );
      // Try 2: ObjectType 이름이 레이어 형식 (l-light, p-cctv 등) 이거나 카테고리명일 때
      // resolveLayerToTemplateKey 로 복수형/접미사 매칭 적용. relationshipUtils 와 동일 정책.
      if (!templateEntry) {
        const resolvedKey = resolveLayerToTemplateKey(objType.name, template.objectTypes);
        if (resolvedKey && template.objectTypes[resolvedKey]) {
          templateEntry = [resolvedKey, template.objectTypes[resolvedKey]] as [string, any];
        }
      }
      if (templateEntry) {
        uuidToTemplateType.set(objType.id, templateEntry[0]);
        log.debug(
          LogCategory.RELATIONSHIP,
          `AutoLink UUID mapping: ${objType.id} (${objType.name}) → ${templateEntry[0]}`,
        );
      } else {
        log.warn(
          LogCategory.RELATIONSHIP,
          `AutoLink UUID mapping FAILED: ${objType.id} (${objType.name}) — no template match. Template displayNames: ${Object.entries(
            template.objectTypes,
          )
            .map(([k, v]: [string, any]) => `${k}="${v.displayName}"`)
            .join(', ')}`,
        );
      }
    });

    // Resolve element's template type key from typeId, objectTypeId, or layer.
    // When CSV elements lack typeId (common after DXF import without type mapping),
    // fall back to layer name → resolveLayerToTemplateKey lookup.
    const resolveTemplateKey = (el: dia.Element): string | undefined => {
      const data = el.get('data') || {};
      const typeId = data.typeId;
      const objectTypeId = el.prop('objectTypeId');

      // 1. Try UUID mapping (typeId or objectTypeId → template key)
      const resolvedId = typeId || objectTypeId;
      if (resolvedId) {
        const mapped = uuidToTemplateType.get(resolvedId);
        if (mapped) return mapped;
        // Direct template key match (e.g., template initialObjects use key as typeId)
        if (template.objectTypes[resolvedId]) return resolvedId;
      }

      // 2. Fallback: use shared layer → template key resolution
      const layer = data.layer;
      if (layer) {
        return resolveLayerToTemplateKey(layer, template.objectTypes);
      }

      return undefined;
    };

    // Debug: log resolution for sample elements
    const sampleElements = graph.getElements().slice(0, 3);
    sampleElements.forEach((el) => {
      const data = el.get('data') || {};
      const resolved = resolveTemplateKey(el);
      log.info(
        LogCategory.RELATIONSHIP,
        `resolveTemplateKey: id=${el.id}, layer=${data.layer}, typeId=${data.typeId} → ${resolved}`,
      );
    });

    log.info(
      LogCategory.RELATIONSHIP,
      `AutoLink mapping summary: ${uuidToTemplateType.size}/${objectTypes.length} UUID mapped. Graph elements: ${graph.getElements().length}`,
    );

    const counts: Record<string, { sources: number; targets: number; estimatedLinks: number }> = {};
    // CANVAS_EDITOR 모드에선 SVG cell view 가 없어 클래스를 붙일 곳이 없다.
    // 반경 밖 타겟 id 를 모아 캔버스 렌더러에 한 번에 넘긴다.
    const canvasRenderer = getFlag('CANVAS_EDITOR') ? getActiveCanvasRenderer() : null;
    const unlinkedTargetIds: string[] = [];
    const newCircles: CirclePreview[] = [];
    const newLines: LinePreview[] = [];
    const colors = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4'];
    let colorIndex = 0;
    // 반경은 표준 스케일 px — 적용(autoLinkAllObjects)과 같은 배율로 graph 좌표로 환산.
    const distanceScale = graphDistanceScale(graph);

    autoLinkRelations.forEach(([key, config]) => {
      if (!config.autoLink) return;

      // Count source elements
      const sourceElements = graph.getElements().filter((el) => {
        const templateTypeKey = resolveTemplateKey(el);
        return templateTypeKey === config.sourceType;
      });

      // Count target elements
      const targetElements = graph.getElements().filter((el) => {
        const templateTypeKey = resolveTemplateKey(el);
        return templateTypeKey === config.targetType;
      });

      log.info(
        LogCategory.RELATIONSHIP,
        `Relation ${key}: sourceType=${config.sourceType}, sources=${sourceElements.length}, targetType=${config.targetType}, targets=${targetElements.length}`,
      );

      // Calculate estimated links within radius and track reachable targets
      const radius = (distances[key] || config.autoLink.maxDistance) * distanceScale;
      const maxCount = parseCardinality(config.cardinality);
      let estimatedLinks = 0;
      const reachableTargetIds = new Set<string>();

      const direction = config.autoLink.direction;

      sourceElements.forEach((srcEl) => {
        const srcCenter = srcEl.getBBox().center();
        const inRange = targetElements.filter((tgtEl) => {
          const tgtCenter = tgtEl.getBBox().center();
          if (srcCenter.distance(tgtCenter) > radius) return false;
          if (direction === 'left' && tgtCenter.x >= srcCenter.x) return false;
          if (direction === 'right' && tgtCenter.x <= srcCenter.x) return false;
          return true;
        });
        // Sort by distance and limit by cardinality
        inRange.sort((a, b) => {
          return (
            srcCenter.distance(a.getBBox().center()) - srcCenter.distance(b.getBBox().center())
          );
        });
        const limited = maxCount === null ? inRange : inRange.slice(0, maxCount);
        limited.forEach((tgtEl) => {
          reachableTargetIds.add(tgtEl.id as string);
          // Store in model coordinates (transform applied via CSS on overlay container)
          const tgtCenter = tgtEl.getBBox().center();
          newLines.push({
            srcX: srcCenter.x,
            srcY: srcCenter.y,
            tgtX: tgtCenter.x,
            tgtY: tgtCenter.y,
            color: colors[colorIndex % colors.length],
            relationKey: key,
          });
        });
        estimatedLinks += limited.length;
      });

      // Highlight unlinked targets on the canvas
      if (enabledRelations[key]) {
        targetElements.forEach((tgtEl) => {
          if (canvasRenderer) {
            if (!reachableTargetIds.has(tgtEl.id as string)) {
              unlinkedTargetIds.push(tgtEl.id as string);
            }
            return;
          }
          const view = paper.findViewByModel(tgtEl);
          if (view && view.el) {
            if (reachableTargetIds.has(tgtEl.id as string)) {
              view.el.classList.remove('unlinked-target');
            } else {
              view.el.classList.add('unlinked-target');
            }
          }
        });
      }

      counts[key] = {
        sources: sourceElements.length,
        targets: targetElements.length,
        estimatedLinks,
      };

      // Create circle previews for each source element
      const color = colors[colorIndex % colors.length];
      colorIndex++;

      sourceElements.forEach((sourceElement) => {
        const bbox = sourceElement.getBBox();
        const center = bbox.center();

        // Store in model coordinates (transform applied via CSS on overlay container)
        newCircles.push({
          x: center.x,
          y: center.y,
          radius,
          color,
          relationKey: key,
        });
      });
    });

    setObjectCounts(counts);
    setCircles(newCircles);
    setLines(newLines);
    canvasRenderer?.setUnlinkedTargetHighlights(unlinkedTargetIds);

    // Cleanup: remove all unlinked-target highlights when dependencies change
    return () => {
      if (canvasRenderer) {
        canvasRenderer.setUnlinkedTargetHighlights([]);
        return;
      }
      if (!graph || !paper) return;
      graph.getElements().forEach((el) => {
        const view = paper.findViewByModel(el);
        if (view && view.el) {
          view.el.classList.remove('unlinked-target');
        }
      });
    };
  }, [graph, paper, template, autoLinkRelations, isOpen, distances, enabledRelations]);

  // Dragging handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).closest('input, button')) return;

      setIsDragging(true);
      setDragOffset({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    },
    [position],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      setPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y,
      });
    },
    [isDragging, dragOffset],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleDistanceChange = (relationKey: string, value: number) => {
    setDistances((prev) => ({
      ...prev,
      [relationKey]: value,
    }));
  };

  const handleConfirm = async () => {
    if (invalidDistance) return;
    // Validation: Check if at least one relation is enabled
    const hasEnabledRelation = Object.values(enabledRelations).some((enabled) => enabled);

    if (!hasEnabledRelation) {
      alert('최소 1개 이상의 관계 타입을 활성화해야 합니다.');
      return;
    }

    setIsLoading(true);

    // Allow UI to update before heavy computation
    await new Promise((resolve) => setTimeout(resolve, 100));

    try {
      // Pass both adjusted distances and enabled relations
      await onConfirm(distances, enabledRelations);
      // Close modal after successful completion
      onClose();
    } catch (error) {
      alert('관계 생성 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  if (!isOpen) return null;

  // Find the element to render circles into. CANVAS_EDITOR 모드에선 paper.el 이
  // visibility:hidden 이라 그 안에 붙인 오버레이도 같이 사라진다 — 캔버스와 같은
  // 컨테이너(paper.el 의 부모)에 붙여야 보인다.
  const paperElement = paper && !getFlag('CANVAS_EDITOR') ? paper.el : null;

  const handleTogglePreview = (relationKey: string) => {
    setShowPreview((prev) => ({
      ...prev,
      [relationKey]: !prev[relationKey],
    }));
  };

  const handleToggleEnabled = (relationKey: string) => {
    setEnabledRelations((prev) => ({
      ...prev,
      [relationKey]: !prev[relationKey],
    }));
  };

  // Master toggle for all enabled relations
  const handleToggleAllEnabled = () => {
    const allEnabled = Object.values(enabledRelations).every((enabled) => enabled);
    const newState: Record<string, boolean> = {};

    Object.keys(enabledRelations).forEach((key) => {
      newState[key] = !allEnabled;
    });

    setEnabledRelations(newState);
  };

  // Master toggle for all previews
  const handleToggleAllPreviews = () => {
    const allEnabled = Object.values(showPreview).every((enabled) => enabled);
    const newState: Record<string, boolean> = {};

    Object.keys(showPreview).forEach((key) => {
      newState[key] = !allEnabled;
    });

    setShowPreview(newState);
  };

  // 오버레이는 paper 와 같은 배율로 축소되므로 테두리·선 두께를 상수 px 로 두면
  // 맵이 축소된 상태(정규화된 큰 좌표계에서 sx 가 0.1 미만인 경우가 흔하다)에서
  // 1px 미만으로 줄어 반경 원이 사실상 보이지 않는다. 화면 기준 두께를 유지한다.
  const onScreen = (px: number) => px / (paperTransform.sx || 1);

  return (
    <>
      {/* Render circle previews on canvas */}
      {paperElement &&
        createPortal(
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              zIndex: 6,
              transform: `translate(${paperTransform.tx}px, ${paperTransform.ty}px) scale(${paperTransform.sx})`,
              transformOrigin: '0 0',
            }}
          >
            {/* Connection lines between sources and targets */}
            <svg
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                overflow: 'visible',
              }}
            >
              <defs>
                <filter id="autoLinkLineGlow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="2" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>
              {lines
                .filter((line) => showPreview[line.relationKey] ?? true)
                .flatMap((line, index) => [
                  // White halo behind for contrast on dark/light backgrounds
                  <line
                    key={`line-halo-${index}`}
                    x1={line.srcX}
                    y1={line.srcY}
                    x2={line.tgtX}
                    y2={line.tgtY}
                    stroke="#FFFFFF"
                    strokeWidth={onScreen(8)}
                    strokeLinecap="round"
                    opacity={0.9}
                  />,
                  // Main colored line with glow
                  <line
                    key={`line-${index}`}
                    x1={line.srcX}
                    y1={line.srcY}
                    x2={line.tgtX}
                    y2={line.tgtY}
                    stroke="#EF4444"
                    strokeWidth={onScreen(5)}
                    strokeLinecap="round"
                    opacity={1}
                    filter="url(#autoLinkLineGlow)"
                  />,
                ])}
            </svg>
            {/* Radius circles */}
            {circles
              .filter((circle) => showPreview[circle.relationKey] ?? true)
              .map((circle, index) => (
                <div key={index}>
                  {/* Outer circle (filled) */}
                  <div
                    style={{
                      position: 'absolute',
                      left: circle.x - circle.radius,
                      top: circle.y - circle.radius,
                      width: circle.radius * 2,
                      height: circle.radius * 2,
                      background: `radial-gradient(circle, ${circle.color}40 0%, ${circle.color}1A 70%, transparent 100%)`,
                      borderRadius: '50%',
                      pointerEvents: 'none',
                      animation: 'pulse 2s ease-in-out infinite',
                    }}
                  />
                  {/* Border circle */}
                  <div
                    style={{
                      position: 'absolute',
                      left: circle.x - circle.radius,
                      top: circle.y - circle.radius,
                      width: circle.radius * 2,
                      height: circle.radius * 2,
                      border: `${onScreen(3)}px solid ${circle.color}`,
                      borderRadius: '50%',
                      opacity: 0.9,
                      pointerEvents: 'none',
                      boxShadow: `0 0 ${onScreen(10)}px ${circle.color}80, inset 0 0 ${onScreen(10)}px ${circle.color}40`,
                    }}
                  />
                  {/* Center dot */}
                  <div
                    style={{
                      position: 'absolute',
                      left: circle.x - onScreen(4),
                      top: circle.y - onScreen(4),
                      width: onScreen(8),
                      height: onScreen(8),
                      background: circle.color,
                      borderRadius: '50%',
                      border: `${onScreen(2)}px solid white`,
                      boxShadow: `0 0 ${onScreen(8)}px ${circle.color}`,
                      pointerEvents: 'none',
                    }}
                  />
                </div>
              ))}
          </div>,
          paperElement,
        )}

      {/* Modal */}
      <div className={styles.overlay}>
        <div
          ref={modalRef}
          className={styles.modal}
          style={{
            position: 'fixed',
            left: position.x,
            top: position.y,
            cursor: isDragging ? 'grabbing' : 'grab',
            opacity: isDragging ? 0.7 : 1,
            transition: isDragging ? 'none' : 'opacity 0.2s ease',
          }}
          onMouseDown={handleMouseDown}
        >
          <div className={styles.header}>
            <h2 className={styles.title}>자동 관계 생성</h2>
            <button className={styles.closeButton} onClick={handleCancel}>
              ✕
            </button>
          </div>

          <div className={styles.content}>
            <p className={styles.description}>
              구역–CCTV는 ID로 연결합니다. 다른 관계는 활성화 후 반경을 직접 입력하거나 슬라이더로
              조절하세요. 반경 단위는 표준 맵 px(주차면 폭 2.5m ≈ 198px)이라 도면을 처음 가져왔을 때와
              저장 후 다시 열었을 때 같은 실제 거리로 계산됩니다. (드래그로 이동 가능)
              CCTV–주차면은 연결 수가 적은 CCTV부터 한 개씩, 10→100→200px 순으로 최대 반경까지 확장해 배정합니다.
              기존 연결을 재배정하려면 먼저 관계 초기화에서 CCTV–주차면만 선택해 초기화하세요.
            </p>

            <div className={styles.zoneSection}>
              <label className={styles.zoneToggle}>
                <input
                  type="checkbox"
                  checked={!!enabledRelations[ZONE_CCTV_ID_RELATION]}
                  onChange={() => handleToggleEnabled(ZONE_CCTV_ID_RELATION)}
                />
                구역–CCTV: ID 기준 자동 연결
              </label>
              <p className={styles.zoneHint}>
                CCTV ID(이름)의 세 번째 항목을 구역 번호로 사용합니다. P1_B1_1_3 → 1구역. 거리는
                사용하지 않습니다. 구역 이름은 1구역 / 구역 1 / 1 형식을 인식하며, 적용하면 기존
                구역 관계를 교체합니다.
              </p>
              {zonePlan && (
                <>
                  <p className={styles.zoneSummary}>
                    연결 예정 {zoneLinkedCount}대 · 미연결 {zoneUnmatched.length}대
                  </p>
                  {zoneUnmatched.length > 0 && (
                    <ul className={styles.zoneUnmatched}>
                      {zoneUnmatched.map((item) => (
                        <li key={String(item.camera.id)}>
                          {item.name || String(item.camera.id)}: {item.reason}
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>

            {/* Master Toggle Controls */}
            <div className={styles.masterControls}>
              <button className={styles.masterToggleButton} onClick={handleToggleAllEnabled}>
                <input
                  type="checkbox"
                  checked={allRelationsEnabled}
                  readOnly
                  className={styles.masterCheckbox}
                />
                <span className={styles.masterToggleText}>
                  {allRelationsEnabled ? '모두 비활성화' : '모두 활성화'}
                </span>
              </button>

              <button className={styles.masterToggleButton} onClick={handleToggleAllPreviews}>
                <input
                  type="checkbox"
                  checked={allPreviewsEnabled}
                  readOnly
                  className={styles.masterCheckbox}
                />
                <span className={styles.masterToggleText}>
                  {allPreviewsEnabled ? '미리보기 모두 해제' : '미리보기 모두 표시'}
                </span>
              </button>
            </div>

            <div className={styles.relationList}>
              {autoLinkRelations.map(([key, config]) => {
                const sourceTypeName =
                  template?.objectTypes?.[config.sourceType]?.displayName || config.sourceType;
                const targetTypeName =
                  template?.objectTypes?.[config.targetType]?.displayName || config.targetType;
                const currentDistance = distances[key] || config.autoLink!.maxDistance;
                const counts = objectCounts[key] || { sources: 0, targets: 0, estimatedLinks: 0 };

                return (
                  <div
                    key={key}
                    className={`${styles.relationItem} ${!enabledRelations[key] ? styles.disabled : ''}`}
                  >
                    <div className={styles.relationHeader}>
                      <div className={styles.relationHeaderTop}>
                        <div className={styles.relationNameGroup}>
                          <span className={styles.relationName}>{config.name}</span>
                          <span className={styles.cardinalityBadge}>{config.cardinality}</span>
                          <span
                            className={`${styles.duplicatesBadge} ${
                              config.autoLink?.allowDuplicates
                                ? styles.duplicatesBadgeAllowed
                                : styles.duplicatesBadgeNotAllowed
                            }`}
                          >
                            {config.autoLink?.allowDuplicates ? '중복 허용' : '중복 불가'}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          {/* Enable toggle */}
                          <label
                            className={`${styles.enableToggle} ${!enabledRelations[key] ? styles.disabled : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={enabledRelations[key] ?? true}
                              onChange={() => handleToggleEnabled(key)}
                              className={styles.enableCheckbox}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span className={styles.enableText}>활성화</span>
                          </label>

                          {/* Preview toggle */}
                          <label className={styles.previewToggleSmall}>
                            <input
                              type="checkbox"
                              checked={showPreview[key] ?? true}
                              onChange={() => handleTogglePreview(key)}
                              className={styles.toggleCheckboxSmall}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span className={styles.toggleTextSmall}>미리보기</span>
                          </label>
                        </div>
                      </div>
                      <span className={styles.relationFlow}>
                        {sourceTypeName} → {targetTypeName}
                      </span>
                      <div className={styles.objectCounts}>
                        <span className={styles.countBadge}>
                          📍 Source: <strong>{counts.sources}</strong>개
                        </span>
                        <span className={styles.countBadge}>
                          🎯 Target: <strong>{counts.targets}</strong>개
                        </span>
                        <span className={styles.countBadge} style={{ color: '#10B981' }}>
                          🔗 예상 관계: <strong>{counts.estimatedLinks}</strong>개
                        </span>
                      </div>
                    </div>

                    <div className={styles.sliderContainer}>
                      {(() => {
                        // 직접 입력한 반경까지 슬라이더 범위를 확장한다.
                        const sliderMax = Math.max(
                          config.autoLink!.maxDistance,
                          AUTO_LINK_SLIDER_MAX_PX,
                          currentDistance,
                        );
                        return (
                          <>
                            <div
                              className={styles.sliderLabel}
                              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                            >
                              <span>탐색 반경:</span>
                              <input
                                type="number"
                                aria-label={`${config.name} 탐색 반경`}
                                min={1}
                                step="any"
                                value={distanceDrafts[key] ?? currentDistance}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  setDistanceDrafts((previous) => ({ ...previous, [key]: raw }));
                                  const value = Number(raw);
                                  if (Number.isFinite(value) && value >= 1)
                                    handleDistanceChange(key, value);
                                }}
                                disabled={!enabledRelations[key]}
                                style={{
                                  width: '72px',
                                  padding: '2px 6px',
                                  border: '1px solid var(--color-border, #555)',
                                  borderRadius: '4px',
                                  background: 'var(--color-bg-secondary, #2a2a2a)',
                                  color: 'var(--color-text, #e0e0e0)',
                                  fontSize: '13px',
                                  fontWeight: 'bold',
                                  textAlign: 'right',
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <span style={{ fontSize: '12px', opacity: 0.7 }}>px</span>
                            </div>
                            <input
                              type="range"
                              min="1"
                              max={sliderMax}
                              step="1"
                              value={Math.min(currentDistance, sliderMax)}
                              onChange={(e) => {
                                setDistanceDrafts((previous) => ({
                                  ...previous,
                                  [key]: e.target.value,
                                }));
                                handleDistanceChange(key, Number(e.target.value));
                              }}
                              className={styles.slider}
                              disabled={!enabledRelations[key]}
                            />
                            <div className={styles.sliderMarks}>
                              <span>1px</span>
                              <span>{sliderMax}px</span>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {invalidDistance && <p role="alert">탐색 반경에 1 이상의 유효한 숫자를 입력하세요.</p>}
          <div className={styles.footer}>
            {isLoading ? (
              <div className={styles.loadingContainer}>
                <div className={styles.spinner}></div>
                <span className={styles.loadingText}>관계 생성 중...</span>
              </div>
            ) : (
              <>
                <button className={styles.cancelButton} onClick={handleCancel} disabled={isLoading}>
                  취소
                </button>
                <button
                  className={styles.confirmButton}
                  onClick={handleConfirm}
                  disabled={isLoading || invalidDistance}
                >
                  관계 생성
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
